import { OnInit, Component, ViewChild, ChangeDetectorRef } from '@angular/core';
import { filterPageTypes, toggleFilterTypes } from 'src/app/store/filters.era';
import { loadLocal } from 'src/app/helpers/helpers';
import { tableFilterTypes } from 'src/app/store/performers.era';
import { JoiEvent, Label, LabelObject } from 'globals/store/events.data';
import { traceNames } from 'src/app/services/performance.tracking.data';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from 'src/app/components/dialog.confirmDelete.component';
import { addAnExhibitor, importTableData, saveTable } from '../performers/performerSharedFunctions';
import { ManageExhibitorData, ManageExhibitorDialogComponent } from './manage-exhibitor';
import { openDialogGetResult } from 'src/app/helpers/angular-helpers';
import { exhibitorColumns, ExhibitorsTableData } from 'src/app/helpers/table-column-types';
import { TableColumn, TableMenuItem } from 'src/app/helpers/table.data';
import { exhibitorColumnsById } from 'src/app/helpers/table-helpers';
import { exhibitorsTableReselector, exhibitorColumnsReselector } from 'src/app/store/reselectors/exhibitors.reselector';
import { Exhibitor } from 'globals/store/exhibitors.data';
import { NestedTableComponent } from './../../components/nested-table/nested-table.component';
import { Wrapper } from 'src/app/components/wrapper.component';
import { LabelUpdateFunctionParams } from '../../components/label-marker/label-maker.dialog.component';
import { addLabelsToMany, removeLabelsFromMany } from '../../helpers/label-helpers';
import { EntitySubTable, exhibitorSubTableTypes, subTableNames } from 'globals/store/entity.data';
import { LoaderPaneFragmentComponent } from 'src/app/components/loader/loader-pane-fragment.component';
import { CommandMenuComponent } from 'src/app/components/nav-bar/command-menu.component';
import { CustomQuestionAnswersComponent } from 'src/app/components/entities/custom-questions-answers.component';
import { JoiNavWrapper } from 'src/app/components/wrappers/joi-nav-wrapper.component';
import { TopMenuComponent } from '../program/top.menu';
import { MobileMenuComponent } from 'src/app/components/menu/mobile-menu.component';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { listDifference, listUnion } from 'globals/helpers/helpers';

@Component({
    selector: 'exhibitors-table',
    template: `
<top-menu></top-menu>
<mobile-menu></mobile-menu>
<joi-nav-wrapper 
  class="icon-tabs"
  [simple]="true"
  [menuItems]="subTables"
  [menuMap]="subTableNames"
  [selectedId]="subTable" 
  (selectItem)="subTable = $event"
></joi-nav-wrapper>
<custom-question-answers *ngIf="subTable === 'responses'" [entityType]="'exhibitor'"></custom-question-answers>
<command-menu
  *ngIf="subTable !== 'responses'"
  [filterPage]="filterPage"
  [filterType]="filterType"
  [columnType]="exhibitorSubTableTypes[subTable]"
  [editColumnsTitle]="('Edit ' + subTableNames[subTable] + ' Columns') | titlecase"
  [filterText]="'Filter exhibitors'"
  [showDottedMenu]="true"
  [showExport]="true"
  [showImport]="true"
  [canEditColumns]="true"
  [addItemText]="!event || event.is_readonly ? '' : '+ ADD AN EXHIBITOR'"
  (addItem)="addExhibitor($event)"
  (onExport)="saveTable()"
  (onImport)="importTableData()"
></command-menu>
<nested-table
  *ngIf="columnData && subTable !== 'responses'"
  [items]="tableData"
  [filtersId]="filterPage"
  [condensedView]="condensed"
  [columns]="columnData[subTable]"
  [readOnly]="!event || event.is_readonly"
  [columnsById]="columnsById"
  [menuItems]="menuItems"
  [menuDisabled]="busy"
  [defaultSelectedColumnId]="exhibitorColumns.name"
  (selectItem)="manageExhibitor($event)"
  (updatedSelectedIds)="selectIds($event)"
  [emptyMessage]="'No exhibitors in this event'"
></nested-table>
<loader-pane-fragment [trace]="traceNames.viewExhibitors"></loader-pane-fragment>
`,
    styleUrls: ['../../page.scss'],
    standalone: true,
    providers: [DatePipe],
    imports: [LoaderPaneFragmentComponent, CommonModule, TitleCasePipe, NestedTableComponent, CommandMenuComponent, CustomQuestionAnswersComponent, JoiNavWrapper, TopMenuComponent, MobileMenuComponent],
})
export class ExhibitorsTableComponent extends Wrapper<ExhibitorsTableComponent> implements OnInit {
  filterPage = filterPageTypes.exhibitors
  filterType = tableFilterTypes.ExhibitorsTable
  subTableNames = subTableNames
  exhibitorColumns = exhibitorColumns
  columnData: { [t in EntitySubTable]: TableColumn[] }
  columnsById: { [id: string]: TableColumn } = exhibitorColumnsById
  tableData: ExhibitorsTableData[] = []
  tableRowsById: { [id:string]: ExhibitorsTableData } = {}
  exhibitors: { [eId: string]: Exhibitor }
  exhibitor_labels: LabelObject = {}
  selectedIds: string[] 
  busy = false
  subTable: EntitySubTable = 'contactDetails'
  subTables: EntitySubTable[] = ['contactDetails', 'publish', 'logistics', 'myView', 'responses' ]
  exhibitorSubTableTypes = exhibitorSubTableTypes
  
  allMenuItems: TableMenuItem[] = [
    { title: 'Delete', method: this.deleteExhibitors.bind(this) },
    // { title: 'Add to clipboard', method: this.addToClipboard.bind(this) },
    // { title: 'Duplicate', method: this.duplicateExhibitors.bind(this) }
  ]
  
  menuItems: TableMenuItem[] = this.allMenuItems // default to showing all menu items 
  condensed: boolean
  event: JoiEvent
  traceNames = traceNames
  @ViewChild(NestedTableComponent) table: NestedTableComponent<ExhibitorsTableData>

  saveTable() {
    saveTable('exhibitor', `${this.event.title} exhibitors table`, this.dialog, this.table)
  }
  importTableData(){
    importTableData('exhibitor', this.dialog, this.exhibitorService.addMultipleExhibitors.bind(this.exhibitorService))
  }

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super()
    this.uiService.changePageTitle("Exhibitors", true)
    this.subProp(this.store.select('events', 'event'), 'event')
    this.subProp(this.store.select('exhibitors', 'entities'), 'exhibitors')
    this.subProp(this.store.select('events', 'labelMaps', 'exhibitor_labels'), 'exhibitor_labels')
    this.selectCallback(exhibitorsTableReselector, null, (data: any) => {
      if (!data) {
        this.tableData = [] // for when the last is deleted
        return
      }
      const { exhibitorTableData, exhibitorTableRowsById } = data
      this.tableData = exhibitorTableData
      this.tableRowsById = exhibitorTableRowsById
    })
    this.loadCondensed()
    this.subCallback(this.store.select('filters', 'toggleFilters', this.filterPage, 'condensed'), _ => this.loadCondensed())
  }

  ngOnInit() {
    this.subCallback(this.store.select(exhibitorColumnsReselector), columnData => {
      this.columnData = columnData
    })
  }

  addExhibitor(event) {
    console.log("NEW EXHIBITOR!")
    addAnExhibitor(event, this.dialog, this.exhibitorService, this.manageExhibitor.bind(this))
  }

  private get selectedExhibitors(): Exhibitor[] {
    return (this.selectedIds || []).map(id => this.exhibitors[id])
  }

  async addToClipboard() {
    this.busy = true
    await this.exhibitorService.templateExhibitors(this.selectedExhibitors)
    this.busy = false
    const n = this.selectedExhibitors.length
    this.table.setMessage(`Templates for ${n} exhibitor${n > 1 ? 's' : ''} added to the clipboard.`)
    this.table.unselectAll()
  }

  async markAsNotConfirmed() {
    const selectedExhibitors = this.selectedExhibitors.filter(e => !!e.confirmed)
    selectedExhibitors.forEach(e => e.confirmed = false)
    await this.exhibitorService.updateExhibitors(selectedExhibitors)
    this.setMenu()
  }

  async markAsConfirmed() {
    const selectedExhibitors = this.selectedExhibitors.filter(e => !e.confirmed)
    selectedExhibitors.forEach(e => e.confirmed = true)
    await this.exhibitorService.updateExhibitors(selectedExhibitors)
    this.setMenu()
  }

  setMenu() {
    setTimeout(() => {
      const selectedExhibitors = this.selectedExhibitors
      this.menuItems = [...this.allMenuItems]     
      if (selectedExhibitors.some(e => e.labels?.length)) this.menuItems.unshift({ title: 'Remove labels', method: this.removeLabels.bind(this) })
      this.menuItems.unshift({ title: 'Add labels', method: this.addLabels.bind(this) })

      if (selectedExhibitors.filter(e => !!e.confirmed).length) 
        this.menuItems.unshift({ title: 'Mark as not confirmed', method: this.markAsNotConfirmed.bind(this) })
      if (selectedExhibitors.filter(e => !e.confirmed).length) 
        this.menuItems.unshift({ title: 'Mark as confirmed', method: this.markAsConfirmed.bind(this) })
    }, 0)
  }

  selectIds(ids: Set<string>) {
    this.selectedIds = Array.from(ids)
    this.setMenu()
  }

  
  public async updateLabelTemplate(labels: Label[]) {
    labels.forEach(label => this.exhibitor_labels[label.id] = label) // need to overwrite the old values here to both maintain order and propagate changes without losing filtered out template labels
    await this.eventService.updateEvent(this.eventService.eventId, { exhibitor_labels: Object.values(this.exhibitor_labels) })
  }

  public async addLabelsUpdateFunction({ labels, labelTemplate, labelsToggled, labelsChanged }: LabelUpdateFunctionParams) {
    if (labelsChanged) this.updateLabelTemplate(labelTemplate)

    if (!labelsToggled) return
    const exhibitors = this.selectedExhibitors
      .filter(e => !e.labels || labels.some(label => !e.labels.includes(label)))
      .map(e => ({...e, labels: listUnion(e.labels, labels) }))
    
    await this.exhibitorService.updateExhibitors(exhibitors)
    this.setMenu()
  }

  public async removeLabelsUpdateFunction({ labels, labelTemplate, labelsToggled, labelsChanged }: LabelUpdateFunctionParams) {
    if (labelsChanged) this.updateLabelTemplate(labelTemplate)

    if (!labelsToggled) return
    const filteredLabels = labelTemplate.map(l => l.id).filter(id => !labels.includes(id))
    if (filteredLabels.length === 0) return

    const exhibitors = this.selectedExhibitors
      .filter(e => e.labels && e.labels.some(label => filteredLabels.includes(label)))
      .map(e => ({...e,  labels: listDifference(e.labels, filteredLabels) }))

    await this.exhibitorService.updateExhibitors(exhibitors)
    this.setMenu()    
  }

  async removeLabels(_, event) { 
    removeLabelsFromMany(this.removeLabelsUpdateFunction.bind(this), this.exhibitor_labels, this.dialog, 'exhibitor', this.selectedExhibitors, event)
  }

  async addLabels(_, event) { 
    addLabelsToMany(this.addLabelsUpdateFunction.bind(this), this.exhibitor_labels, this.dialog, 'exhibitor', this.selectedExhibitors, event)
  }

  async duplicateExhibitors(toDuplicate, event) { // not in use as multiple duplicates at one time can lead to crazy results
    const data: ConfirmDeleteData = {
      title: `Duplicate ${toDuplicate.length} exhibitor${toDuplicate.length === 1 ? '' : 's'}`,
      rect: event.target.getBoundingClientRect(),
      para: `Are you sure you want to duplicate ${toDuplicate.length > 1 ? `all ${toDuplicate.length} selected exhibitors` : 'the selected exhibitor'}?`,
      dialog: this.dialog,
      deleteButtonText: 'DUPLICATE',
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    this.exhibitorService.duplicateExhibitors(toDuplicate.map(({ exhibitor }) => exhibitor))
  }

  async manageExhibitor({ id }: ExhibitorsTableData) {
    console.log(`Should be opening the manage exhibitor dialog for exhibitor ${id}`)
    const data: ManageExhibitorData = { exhibitorId: id }
    this.changeDetectorRef.detach()
    await openDialogGetResult(this.dialog, ManageExhibitorDialogComponent, { autoFocus: false, disableClose: true, data, panelClass: 'full-screen-modal' })
    this.changeDetectorRef.reattach()
  }

  async deleteExhibitors(toDelete: ExhibitorsTableData[], event) {
    const data: ConfirmDeleteData = {
      title: `Delete ${toDelete.length} exhibitors`,
      rect: event.target.getBoundingClientRect(),
      para: `Are you sure you want to delete ${toDelete.length > 1 ? `all ${toDelete.length} selected exhibitors` : 'the selected exhibitor'}?`,
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    this.exhibitorService.deleteExhibitors(toDelete.map(({ exhibitor }) => exhibitor))
  }

  loadCondensed() {
    this.condensed = loadLocal(`${this.filterPage}:${toggleFilterTypes.condensed}`, false)
  }

}
